import { useState } from 'react';
import { SelectOption } from '../searchableSelect/searchableSelectTypes';
import { getTranslationFromTranslatableEntity } from '../../constants/matter';
import { useLanguage } from '../../context/LanguageContext';
import { useManageMatterRequestContext } from '../../context/ManageMatterRequest';
import { useFetchBusinessAreas } from '../../hooks/useFetchBusinessAreas';
import { IBusinessArea } from '../../interface/businessArea';
import { EntityStatus } from '../../interface/common';
import { IMatterRequest } from '../../interface/matterRequest';

/** The props `SearchableSelect` needs to pick a matter request's business area, from the same list the picker screen uses. */
export const useMatterRequestBusinessAreaSelect = () => {
  const { matterRequest, updateManageMatterRequest } = useManageMatterRequestContext();
  const { language: appLanguage } = useLanguage();
  const [searchValue, setSearchValue] = useState('');
  const {
    data: businessAreas,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useFetchBusinessAreas(
    { pageSize: 50, search: searchValue, extraFilters: { status: EntityStatus.ACTIVE } },
    true,
  );

  const toOption = (businessArea: IBusinessArea): SelectOption => ({
    value: businessArea.id,
    label: getTranslationFromTranslatableEntity(businessArea.translations, appLanguage),
  });

  const options = businessAreas.map(toOption);
  const value =
    matterRequest?.businessAreaId && matterRequest.businessArea
      ? toOption(matterRequest.businessArea)
      : null;

  const onChange = (option: SelectOption) => {
    const businessArea = businessAreas.find(({ id }: IBusinessArea) => id === option.value);
    if (!businessArea) return;
    updateManageMatterRequest({
      ...matterRequest,
      businessAreaId: businessArea.id,
      businessArea,
      isBusinessAreaSetByUser: true,
    } as unknown as IMatterRequest);
  };

  return {
    options,
    value,
    onChange,
    onSearch: setSearchValue,
    onLoadMore: fetchNextPage,
    hasMore: Boolean(hasNextPage),
    loading: isLoading,
    loadingMore: isFetchingNextPage,
  };
};
